import styled, { css } from 'styled-components';

const gridTemplatesLarge = {
  'mix-4': `
    "card1 card2 card2"
    "card1 card3 card4"
  `,
  'vertical-4': `
    "card1 card3 card2"
    "card1 card4 card2"
  `,
  'horizontal-4': `
    "card3 card1 card1"
    "card2 card2 card4"
  `,
  'vertical-5': `
    "card1 card2 card3"
    "card1 card4 card5"
  `,
  'horizontal-5': `
    "card2 card1 card1"
    "card3 card4 card5"
  `,
};

const gridTemplatesMd = {
  4: `
    "card1 card1"
    "card3 card4"
    "card2 card2"
  `,
  5: `
    "card1 card1"
    "card2 card3"
    "card4 card5"
  `,
};

const gridTemplatesSm = {
  4: `
    "card1"
    "card1"
    "card3"
    "card4"
    "card2"
    "card2"
  `,
  5: `
    "card1"
    "card1"
    "card2"
    "card3"
    "card4"
    "card5"
  `,
};

const orientationSettings = {
  vertical: {
    flex: 'column',
    clip: 'ellipse(100% 100% at 50% 100%)',
  },
  horizontal: {
    flex: 'row',
    clip: 'circle(1100px at 1100px 50%)',
  },
};

export const Grid = styled.div`
  display: grid;
  gap: 16px;
  padding-top: var(--ui-spacings-lg);
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, minmax(auto, 320px));
  grid-template-areas:
    'card1 card3 card2'
    'card1 card4 card2';

  ${(props) =>
    props.$count === 5 &&
    css`
      grid-template-areas:
        'card1 card2 card3'
        'card1 card4 card5';
    `}

  grid-template-areas: ${(props) => gridTemplatesLarge[`${props.$orientation}-${props.$count}`]};

  @media (max-width: ${({ theme }) => theme.breakpoints.max_lg}px) {
    grid-template-rows: repeat(2, minmax(320px, auto));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, auto);
    padding-top: var(--ui-spacings-sm);
    grid-template-areas: ${(props) => gridTemplatesMd[props.$count]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    padding-top: 8px;
    grid-template-areas: ${(props) => gridTemplatesSm[props.$count]};
  }

  ${(props) =>
    props.$count === 5 &&
    css`
      & > *:nth-child(n + 2) ${ImageWrapper} {
        display: none !important;
      }
    `}
`;

export const ContentWrapper = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

export const ImageWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: flex-end;
  position: relative;
  width: 100%;

  .cardImage {
    display: flex;
    justify-content: flex-end;
    object-fit: contain;
    object-position: center bottom;

    @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
      position: relative;
      width: auto;
      object-position: right bottom;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
      position: unset !important;
    }

    ${(props) =>
      props.index === 1 &&
      css`
        object-position: right bottom;
      `}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    justify-content: center;
    align-items: center;
    width: unset;
  }
`;

export const Card = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  width: 100%;
  border-radius: 32px;
  background: ${({ theme }) => theme.colors.neutral[200]};
  overflow: hidden;
  background: ${({ $colored, theme }) =>
    $colored ? theme.colors.neutral[200] : theme.colors.white};
  
  grid-area: ${({ $index }) => `card${$index + 1}`};

  ${({ $orientation, $index, theme: { breakpoints } }) => {
    let config = orientationSettings[$orientation] || {};

    if ($orientation === 'mix') {
      const mode = $index < 1 ? 'vertical' : 'horizontal';
      config = orientationSettings[mode];
    }

    return css`
      flex-direction: ${config.flex};

      ${ImageWrapper} {
        clip-path: ${config.clip};
      }

      @media (max-width: ${breakpoints.max_md}px) {
        flex-direction: row;

        ${ImageWrapper} {
          clip-path: circle(1100px at 1100px 50%);
        }
      }
      @media (max-width: ${breakpoints.max_sm}px) {
        flex-direction: column;

        ${ImageWrapper} {
          clip-path: circle(1100px at 50% 1100px);
        }
      }
    `;
  }}
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
    flex-direction: row;

    ${ImageWrapper} {
      clip-path: circle(1100px at 1100px 50%);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    flex-direction: column;

    ${ImageWrapper} {
      clip-path: circle(1100px at 50% 1100px);
    }
  }
`;

export const TitlesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ui-spacings-sm);
  height: 100%;
  padding: var(--ui-spacings-2xl);
  text-align: center;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    align-items: center;
    padding: var(--ui-spacings-lg);
  }

  ${(props) =>
    props.index === 0 &&
    css`
      padding: 48px 48px 0px;

      @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
        padding: 32px 0 32px 32px;
      }

      @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
        padding: 32px 32px 8px 32px;
      }
    `}

  ${(props) =>
    props.index === 1 &&
    css`
      padding: 48px 24px 32px 48px;

      @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
        padding: 32px 0 32px 32px;
      }

      @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
        padding: 32px 32px 8px 32px;
      }
    `}
`;

export const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
  max-width: 309px;

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    max-width: unset;
    width: 100%;
    align-items: center;
  }

  h3 {
    font-size: 40px;
    line-height: 48px;
    letter-spacing: -1px;

    @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
      font-size: 28px;
      line-height: 32px;
      letter-spacing: -0.5px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
      text-align: center;
      font-size: 22px;
      line-height: 26px;
    }
  }

  p {
    @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
      font-size: 14px;
      line-height: 24px;
      text-align: center;
    }
  }
`;
